import {m as motion} from 'motion/react';
import {useResizeObserver} from 'usehooks-ts';
import type {Map as MapboxMap} from 'mapbox-gl';
import ReactCountryFlag from 'react-country-flag';
import {useCallback, useMemo, useRef} from 'react';
import {arrowDownSLine, historyLine, thumbDownLine, thumbUpLine, timeLine} from 'src/utils/icons';
import useSearchParam, {setSearchParam} from 'src/hooks/useSearchParam';
import MapPlacesList, {type PlacesItemData} from './MapPlacesList';
import useWidthBreakpoints from 'src/utils/useWidthBreakpoints';
import getReviewsByPlaceId from 'src/utils/getReviewsByPlaceId';
import Dropdown, {type FullDropdownMenuItem} from '../Dropdown';
import useMapSelectedPlace from './useMapSelectedPlace';
import {tabContentMotionProps} from '../common/Tabs';
import {filterPlaces} from './useFilteredPlaces';
import useMounted from 'src/hooks/useMounted';
import useMapUtils from './useMapUtils';
import BaseButton from '../BaseButton';
import RemixIcon from '../RemixIcon';

const sortPlaces = (places: Place[], reviewsByPlaceId: {[placeId: string]: Review}, sort: string) => {
  if (sort === 'recent' || sort === 'oldest')
    return places.sort((a, b) => {
      if (!a.dateAdded || !b.dateAdded) return 0;

      const multiplier = sort === 'recent' ? 1 : -1;

      return (new Date(b.dateAdded).getTime() - new Date(a.dateAdded).getTime()) * multiplier;
    });

  if (sort === 'best' || sort === 'worst')
    return places.sort((a, b) => {
      const aReview = reviewsByPlaceId[a._id];
      const bReview = reviewsByPlaceId[b._id];

      if (!aReview || !bReview) return 0;

      const multiplier = sort === 'best' ? 1 : -1;

      return (bReview.mainRating - aReview.mainRating) * multiplier;
    });

  return places;
};

const getCountries = (places: Place[]) => {
  const countries: {[countryCode: string]: {name: string; countryCode: string; count: number}} = {};

  for (const place of places) {
    const countryCode = place?.countryCode;

    if (!countryCode) continue;

    countries[countryCode] = {
      name: place?.countryName,
      countryCode: place?.countryCode,
      count: (countries[countryCode]?.count || 0) + 1
    };
  }

  return Object.values(countries).sort((a, b) => b.count - a.count);
};

type Props = {
  places: Place[];
  reviews: Review[];
  showSortControl?: boolean;
  map: MapboxMap | null;
};

const MapReviewedPlacesList = ({places, reviews, showSortControl, map}: Props) => {
  // @ts-expect-error
  const ref = useRef<HTMLDivElement>();
  const isMounted = useMounted({delayMs: 200});
  const mobileBreakpoint = useWidthBreakpoints([800], 1);
  const {height: containerHeight = 1000} = useResizeObserver({ref});

  const {setSelectedPlace} = useMapSelectedPlace({places, map});
  const {zoomToPlaces} = useMapUtils(map);

  const isMobile = mobileBreakpoint === 0;

  const reviewsByPlaceId = useMemo(() => getReviewsByPlaceId(reviews), [reviews]);

  const initialNumberOfItems = isMobile ? 6 : 20;
  const maxNumberOfItems = isMounted ? places.length : initialNumberOfItems;

  const [typeFilter, setTypeFilter] = useSearchParam('placeType');
  const [countryFilter, setCountryFilter] = useSearchParam('country');
  const [sort, setSort] = useSearchParam('sort', 'recent');

  const setFilter = useCallback(
    (type: string = typeFilter, countryCode: string = countryFilter) => {
      setTypeFilter(type);
      setCountryFilter(countryCode);
      setSearchParam('selectedPlace', '');
      zoomToPlaces(filterPlaces(places, type, countryCode));
    },
    [zoomToPlaces, typeFilter, countryFilter]
  );

  const itemData = useMemo<PlacesItemData>(
    () => ({
      places: sortPlaces(filterPlaces(places, typeFilter, countryFilter), reviewsByPlaceId, sort).slice(
        0,
        maxNumberOfItems
      ),
      reviewsByPlaceId,
      setSelectedPlace
    }),
    [places, reviewsByPlaceId, sort, maxNumberOfItems, typeFilter, countryFilter, setSelectedPlace]
  );

  const numberOfItems = itemData.places.length;

  const countriesObject: {[countryCode: string]: FullDropdownMenuItem} = useMemo(
    () => ({
      all: {text: 'All countries', onAction: () => setFilter(undefined, '')},
      ...getCountries(places).reduce(
        (acc, country) => {
          acc[country.countryCode] = {
            text: country.name,
            iconComponent: (
              <ReactCountryFlag
                countryCode={country.countryCode}
                style={{width: 18, height: 'auto', marginRight: 8, marginBottom: 2}}
                className="rounded-[3px] shadow-[0px_1px_2px_rgba(0,0,0,0.08),0px_3px_7px_rgba(0,0,0,0.1)]"
                svg
              />
            ),
            onAction: () => setFilter(undefined, country.countryCode)
          };

          return acc;
        },
        {} as {[countryCode: string]: FullDropdownMenuItem}
      )
    }),
    [places, setFilter]
  );

  const sortItemsObject: {[name: string]: FullDropdownMenuItem} = {
    recent: {text: 'Recent', icon: timeLine, onAction: () => setSort('')},
    oldest: {text: 'Oldest', icon: historyLine, onAction: () => setSort('oldest')},
    best: {text: 'Best rated', icon: thumbUpLine, onAction: () => setSort('best')},
    worst: {text: 'Worst rated', icon: thumbDownLine, onAction: () => setSort('worst')}
  };

  return (
    <motion.div
      className="relative grow flex flex-col -mt-2 max-[800px]:mt-0 w-[calc(100%+16px)] -ml-4 -mr:4 origin-[center_100px]"
      {...tabContentMotionProps}
    >
      <div className="flex justify-between py-6 -my-5 px-4 gap-2 relative z-1 overflow-x-auto no-scrollbar">
        <Dropdown items={Object.values(countriesObject)} align="start">
          <BaseButton buttonVariant={countryFilter ? 'primary' : 'text'} size="small" className="px-3">
            {countryFilter ? countriesObject[countryFilter]?.text || 'Country' : 'Country'}

            <RemixIcon
              icon={arrowDownSLine}
              size={18}
              className="ml-1 -mr-1"
              color={countryFilter ? 'white' : 'var(--text-color)'}
            />
          </BaseButton>
        </Dropdown>

        {showSortControl ? (
          <Dropdown items={Object.values(sortItemsObject)}>
            <BaseButton buttonVariant="text" size="small" className="px-3">
              <RemixIcon icon={sortItemsObject[sort]?.icon} size={18} className="mr-2" color="var(--text-color)" />
              {sortItemsObject[sort].text || 'Recent'}
            </BaseButton>
          </Dropdown>
        ) : null}
      </div>

      <div className="relative h-0 grow mx-2 max-[800px]:h-auto" ref={ref}>
        <MapPlacesList
          virtualScroll={!isMobile}
          numberOfItemsToRender={numberOfItems}
          containerHeight={containerHeight}
          itemData={itemData}
        />

        <div className="absolute top-0 w-full h-5 bg-linear-to-t from-transparent to-(--page-background) pointer-events-none" />
      </div>
    </motion.div>
  );
};

export default MapReviewedPlacesList;
